/**
 * Selenology & Lunar Geology Field Guide Panel
 * Explains lunar physical parameters, geological stratigraphy,
 * crustal asymmetry, and the Lunar Magma Ocean hypothesis.
 */

import { GEOLOGICAL_ERAS } from '../data/lunarFeatures.js';

export class SelenologyPanel {
  constructor(container) {
    this.container = container;

    this.element = document.createElement('div');
    this.element.className = 'side-panel selenology-panel glass-panel hidden';
    this.container.appendChild(this.element);

    this.render();
  }

  render() {
    this.element.innerHTML = `
      <div class="panel-header">
        <div class="title-wrap">
          <span class="panel-tag">SELENOLOGY</span>
          <h2>Lunar Geology & Physics</h2>
        </div>
        <button class="panel-close-btn" id="seleno-close">&times;</button>
      </div>

      <div class="panel-content">
        <!-- Physical Parameters Table -->
        <div class="seleno-card">
          <h4>PHYSICAL CONSTANTS</h4>
          <table class="params-table">
            <tbody>
              <tr><td>Mean Radius</td><td>1,737.4 km (0.273 Earth)</td></tr>
              <tr><td>Surface Area</td><td>3.79 × 10⁷ km² (≈ Africa)</td></tr>
              <tr><td>Mass</td><td>7.342 × 10²² kg (0.0123 Earth)</td></tr>
              <tr><td>Surface Gravity</td><td>1.62 m/s² (0.166 g)</td></tr>
              <tr><td>Escape Velocity</td><td>2.38 km/s</td></tr>
              <tr><td>Atmosphere</td><td>10⁻¹⁰ Pa (Hard Vacuum)</td></tr>
              <tr><td>Day/Night Temp</td><td>-130°C to +120°C (27 K in PSRs)</td></tr>
              <tr><td>Axial Tilt to Ecliptic</td><td>1.54° (No seasonal swings)</td></tr>
            </tbody>
          </table>
        </div>

        <!-- Geological Stratigraphy -->
        <div class="seleno-card">
          <h4>CHRONOSTRATIGRAPHY (GEOLOGICAL ERAS)</h4>
          <div class="eras-list">
            ${GEOLOGICAL_ERAS.map(era => `
              <div class="era-item">
                <div class="era-header">
                  <span class="era-name">${era.name}</span>
                  <span class="era-age">${era.age}</span>
                </div>
                <p class="era-desc">${era.desc}</p>
              </div>
            `).join('')}
          </div>
        </div>

        <!-- Crustal Asymmetry & Tidal Locking -->
        <div class="seleno-card">
          <h4>CRUSTAL ASYMMETRY & TIDAL LOCKING</h4>
          <p>
            The Moon is tidally locked to Earth in a 1:1 spin-orbit resonance, so the same hemisphere permanently faces Earth.
          </p>
          <div class="asymmetry-grid">
            <div class="asymmetry-box near">
              <h5>Nearside (Visible)</h5>
              <p>Thinner crust (~30–40 km). Enriched in heat-producing radioactive elements (Thorium, Potassium) known as the <strong>Procellarum KREEP Terrane (PKT)</strong>. Extensive dark basaltic maria cover ~31% of the surface.</p>
            </div>
            <div class="asymmetry-box far">
              <h5>Farside (Hidden)</h5>
              <p>Thicker crust (~60–80 km). The thick anorthositic crust prevented magma from easily ascending to the surface, leaving the farside dominated by ancient, heavily cratered highlands with almost no large maria.</p>
            </div>
          </div>
        </div>

        <!-- Lunar Magma Ocean Hypothesis -->
        <div class="seleno-card">
          <h4>THE LUNAR MAGMA OCEAN</h4>
          <p>
            Following the Giant Impact (Theia hypothesis ~4.51 Ga), the newborn Moon was engulfed in a global magma ocean hundreds of kilometers deep. As it cooled, dense minerals (olivine, pyroxene) sank to form the mantle, while buoyant plagioclase feldspar floated to the surface like icebergs, crystallizing into the pale anorthosite highlands we still see today.
          </p>
        </div>
      </div>
    `;

    this.initEvents();
  }

  initEvents() {
    this.element.querySelector('#seleno-close').addEventListener('click', () => this.hide());
  }

  toggle() {
    this.element.classList.toggle('hidden');
  }

  show() {
    this.element.classList.remove('hidden');
  }

  hide() {
    this.element.classList.add('hidden');
  }
}
